import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import SlipToggle, { betLegFrom } from "@/components/SlipToggle";
import { useDisplay } from "@/context/DisplayContext";
import TrackToggle from "@/components/TrackToggle";
import { Loader2, TrendingUp, Users, ChevronRight, ShieldCheck, Gauge, Flame } from "lucide-react";

const fmtDay = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });

const RISK_META = {
  low: { label: "Low", color: "#34C759", icon: ShieldCheck },
  medium: { label: "Medium", color: "#FF9500", icon: Gauge },
  high: { label: "High", color: "#FF375F", icon: Flame },
};

function ValueCard({ p, index, onOpen }) {
  const { fmtProb } = useDisplay();
  const rm = RISK_META[p.risk];
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
      onClick={onOpen}
      data-testid="value-bet-card"
      role="button"
      className="glass rounded-3xl p-5 text-left hover:-translate-y-0.5 transition-transform cursor-pointer"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {p.photo ? (
            <img src={p.photo} alt="" className="h-11 w-11 rounded-full object-cover shrink-0" />
          ) : (
            <div className="h-11 w-11 rounded-full bg-secondary flex items-center justify-center shrink-0">
              <Users className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-display font-bold truncate">{p.player}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-1.5">
              {p.prop}
              {rm && (
                <span
                  className="text-[10px] font-semibold rounded-full px-1.5 py-0.5"
                  style={{ color: rm.color, backgroundColor: rm.color + "22" }}
                >
                  {rm.label}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-extrabold text-2xl text-[#34C759]">+{p.value_pct}%</div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">value</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3.5 text-center">
        <div className="rounded-xl bg-secondary/50 py-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Yours</div>
          <div className="font-bold text-sm">{fmtProb(p.recent.pct)}</div>
        </div>
        <div className="rounded-xl bg-secondary/50 py-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Book</div>
          <div className="font-bold text-sm">{fmtProb(p.implied_pct)}</div>
        </div>
        <div className="rounded-xl bg-primary/10 py-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Odds</div>
          <div className="font-bold text-sm text-primary">{p.odd != null ? `@${p.odd.toFixed(2)}` : "-"}</div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        <div className="text-xs min-w-0 truncate">
          <Badge variant="outline" className="rounded-full text-[10px] mr-1.5">
            {p.tournament}
          </Badge>
          <span className="text-muted-foreground">
            {p.home} v {p.away} · {fmtDay(p.date)}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <TrackToggle p={p} />
          <SlipToggle leg={betLegFrom(p)} />
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </motion.div>
  );
}

export default function ValueBets() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/value-bets")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const picks = data?.picks || [];

  return (
    <div className="space-y-6" data-testid="value-bets-page">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-[#34C759]/15 flex items-center justify-center shrink-0">
          <TrendingUp className="h-6 w-6 text-[#34C759]" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Value Finder</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Props where your hit-rate beats the bookmaker's implied odds, positive expected value.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex flex-col items-center justify-center gap-2 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-xs text-muted-foreground">
            Comparing your hit-rates against live bookmaker odds…
          </p>
        </div>
      ) : picks.length === 0 ? (
        <div className="glass rounded-3xl p-12 text-center" data-testid="value-bets-empty">
          <TrendingUp className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-display text-xl font-bold">No value bets right now</h3>
          <p className="text-muted-foreground text-sm mt-1">
            We scanned {data?.fixtures_scanned || 0} upcoming fixtures but found no props priced above their
            fair value yet. Odds appear closer to kickoff, check back nearer match day.
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {picks.length} value {picks.length === 1 ? "edge" : "edges"} found across {data.fixtures_scanned} fixtures ·
            "Value" = your probability × odds − 1. Tap a pick to open the matchup.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="value-bets-list">
            {picks.map((p, i) => (
              <ValueCard
                key={`${p.fixture_id}-${p.player}-${p.prop_key}`}
                p={p}
                index={i}
                onOpen={() => navigate(`/tournaments/${encodeURIComponent(p.tournament)}`)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
